import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { usePackages } from '../hooks/usePackages';
import { useAuth } from '../contexts/AuthContext';
import Logo from '../components/icons/Logo';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { SunIcon, MoonIcon, CheckCircleIcon, ArrowRightIcon, GlobeAltIcon } from '@heroicons/react/24/outline';

const Pricing = () => {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const { packages, loading: packagesLoading } = usePackages();

  const handleSelectPackage = (packageType: string) => {
    if (user) {
      navigate(`/onboarding?plan=${packageType}`);
    } else {
      navigate(`/login?mode=register&plan=${packageType}`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-teal-50 dark:from-gray-950 dark:via-slate-900 dark:to-gray-950">
      {/* Header */}
      <div className="absolute top-4 right-4 z-20 flex items-center space-x-3">
        <button
          onClick={toggleTheme}
          className="p-2 rounded-lg bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm hover:bg-white/90 dark:hover:bg-gray-800/90 transition-all duration-200 shadow-md"
        >
          {theme === 'light' ? (
            <MoonIcon className="h-5 w-5 text-gray-700 dark:text-gray-300" />
          ) : (
            <SunIcon className="h-5 w-5 text-gray-700 dark:text-gray-300" />
          )}
        </button>
        <button
          onClick={() => setLanguage(language === 'hu' ? 'en' : 'hu')}
          className="p-2 rounded-lg bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm hover:bg-white/90 dark:hover:bg-gray-800/90 transition-all duration-200 shadow-md flex items-center space-x-1"
        >
          <GlobeAltIcon className="h-5 w-5 text-gray-700 dark:text-gray-300" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 uppercase">
            {language === 'hu' ? 'EN' : 'HU'}
          </span>
        </button>
        <Link
          to="/login"
          className="px-4 py-2 text-sm font-medium text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300"
        >
          {t('saas.pricing.login')}
        </Link>
      </div>

      {/* Hero */}
      <div className="max-w-5xl mx-auto px-4 pt-20 pb-8 text-center">
        <div className="flex justify-center mb-4">
          <Logo className="h-16" />
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-3">
          {t('saas.pricing.title')}
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
          {t('saas.pricing.subtitle')}
        </p>

        {/* Billing cycle toggle */}
        <div className="inline-flex items-center space-x-1 mt-6 p-1 bg-white dark:bg-gray-800 rounded-lg shadow-md">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-5 py-2 rounded-md text-sm font-medium transition-all ${
              billingCycle === 'monthly'
                ? 'bg-cyan-600 text-white'
                : 'text-gray-600 dark:text-gray-300'
            }`}
          >
            {t('saas.pricing.monthly')}
          </button>
          <button
            onClick={() => setBillingCycle('yearly')}
            className={`px-5 py-2 rounded-md text-sm font-medium transition-all ${
              billingCycle === 'yearly'
                ? 'bg-cyan-600 text-white'
                : 'text-gray-600 dark:text-gray-300'
            }`}
          >
            {t('saas.pricing.yearly')}
            <span className="ml-1 text-xs text-green-500">{t('saas.pricing.yearlyDiscount')}</span>
          </button>
        </div>
      </div>

      {/* Package cards */}
      <div className="max-w-6xl mx-auto px-4 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-4xl mx-auto">
          {packagesLoading ? (
            <div className="col-span-full text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-500">{t('saas.pricing.loadingPackages')}</p>
            </div>
          ) : (
          packages.filter(p => p.isActive).map((pkg) => {
            const monthlyPrice = billingCycle === 'monthly'
              ? pkg.price.monthly
              : Math.round(pkg.price.yearly / 12);

            return (
              <div
                key={pkg.id}
                className={`relative bg-white dark:bg-gray-800 rounded-2xl shadow-lg border-2 transition-all duration-300 hover:shadow-xl ${
                  pkg.isPopular
                    ? 'border-cyan-500 ring-2 ring-cyan-500/20 scale-105'
                    : 'border-gray-200 dark:border-gray-700'
                }`}
              >
                {pkg.isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-3 py-1 bg-gradient-to-r from-blue-600 to-cyan-600 text-white text-xs font-bold rounded-full shadow-md">
                      {t('saas.pricing.popular')}
                    </span>
                  </div>
                )}

                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">
                    {pkg.name}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 min-h-[40px]">
                    {pkg.description}
                  </p>

                  <div className="mb-4">
                    {pkg.price.monthly === 0 ? (
                      <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{t('saas.pricing.free')}</p>
                    ) : (
                      <>
                        <div className="flex items-baseline">
                          <span className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                            {monthlyPrice.toLocaleString('hu-HU')}
                          </span>
                          <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">{t('saas.pricing.perMonth')}</span>
                        </div>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{t('saas.pricing.plusVAT')}</p>
                      </>
                    )}
                    {billingCycle === 'yearly' && pkg.price.monthly > 0 && (
                      <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                        {(pkg.price.yearly).toLocaleString('hu-HU')} {t('saas.pricing.perYear')} {t('saas.pricing.plusVAT')}
                      </p>
                    )}
                  </div>

                  <button
                    onClick={() => handleSelectPackage(pkg.type)}
                    className={`w-full py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                      pkg.isPopular
                        ? 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white shadow-md hover:shadow-lg'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {pkg.price.monthly === 0 ? t('saas.pricing.startFree') : t('saas.pricing.startTrial')}
                  </button>

                  <div className="mt-5 space-y-2">
                    <div className="flex items-center text-sm">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>{pkg.maxUsers === -1 ? t('saas.pricing.unlimited') : pkg.maxUsers}</strong> {t('saas.pricing.users')}
                      </span>
                    </div>
                    <div className="flex items-center text-sm">
                      <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300">
                        <strong>{pkg.maxStorage === -1 ? t('saas.pricing.unlimited') : pkg.maxStorage}</strong> {t('saas.pricing.storage')}
                      </span>
                    </div>
                    {pkg.features.map((feature: string, i: number) => (
                      <div key={i} className="flex items-start text-sm">
                        <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600 dark:text-gray-400">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })
          )}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
            {t('saas.pricing.notSure')}
          </p>
          <button
            onClick={() => handleSelectPackage('pro')}
            className="inline-flex items-center px-6 py-3 text-base font-medium text-white bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 rounded-xl shadow-lg hover:shadow-xl transition-all"
          >
            {t('saas.pricing.startNow')}
            <ArrowRightIcon className="h-5 w-5 ml-2" />
          </button>
        </div>

      </div>
    </div>
  );
};

export default Pricing;
